import { Spacing, Typography } from '@persona-id/design-tokens';
import { ComponentType, ReactNode, SVGAttributes } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import { Heading, Paragraph, Span } from 'design-system/components/Text';
import { TransitionDuration } from 'design-system/variables/transitions';
import APIImage from 'images/dashboard/getting-started/api-only.svg';
import EmbeddedFlowImage from 'images/dashboard/getting-started/embedded-flow.svg';
import HostedFlowImage from 'images/dashboard/getting-started/hosted-flow.svg';
import MobileSDKsImage from 'images/dashboard/getting-started/mobile-sdks.svg';

export default () => {
  return (
    <Card>
      <IntegrationCardBody>
        <IntegrationCardHeader>Overview</IntegrationCardHeader>
        <Paragraph color='primary'>
          Integrating Persona into your user experience always starts with creating an Inquiry to
          collect user data, and then proceeding accordingly based on the status of the Inquiry.
        </Paragraph>
        <Paragraph color='primary'>
          Any downstream Verifications or Reports that are run, or any decisioning done in Workflows
          or Cases, will all impact the <b>Inquiry status</b>. This allows you to use the Inquiry
          status as a source of truth to understand how a user is moving through your Persona
          solution. The steps below outline a sample implementation with these principles in mind.
        </Paragraph>
        <Spacer height={1} />
        <IntegrationCardHeader>Step 1: Choose a Reference ID</IntegrationCardHeader>
        <Paragraph color='primary'>
          A Reference ID is a string used to identify a user within Persona in a way that aligns
          with your business. You will pass in the Reference ID every time you create an Inquiry. We
          recommend using your own internal user ID number as the Reference ID.
        </Paragraph>
        <Spacer height={1} />
        <IntegrationCardHeader>
          Step 2: Determine how your Inquiries will be created
        </IntegrationCardHeader>
        <Paragraph color='primary'>There are two ways to create an Inquiry:</Paragraph>
        <UnorderedList>
          <ListItem>
            <b>Make a Server API call</b>: This creates an Inquiry token that you will use to
            surface the Inquiry to your end user.
          </ListItem>
          <ListItem>
            <b>Create the Inquiry client-side</b>: Use the Inquiry Template ID to create a
            just-in-time Inquiry. When your end user accesses the Persona flow, a new Inquiry will
            automatically be created using the provided template.
          </ListItem>
        </UnorderedList>
        <Paragraph color='primary'>
          See our{' '}
          <a
            href='https://docs.withpersona.com/docs/creating-inquiries'
            target='_blank'
            rel='noreferrer'
          >
            documentation
          </a>{' '}
          for more information on creating Inquiries.
        </Paragraph>
        <Spacer height={1} />
        <IntegrationCardHeader>
          Step 3: Add the Inquiry to your user experience
        </IntegrationCardHeader>
        <Paragraph color='primary'>
          Based on the type of product or service your business offers, select and follow one of
          these integration methods:
        </Paragraph>
        <Spacer height={1} />
        <IntegrationComparisonCardsWrapper>
          <Link to='/dashboard/getting-started/hosted-flow'>
            <IntegrationComparisonCard
              title='Hosted Flow'
              image={HostedFlowImage}
              stickerLabel='Any platform'
            >
              <IntegrationComparisonCardDescription>
                <Paragraph>
                  Send a link to a Persona-hosted user experience to collect user information. Then,
                  use Persona to verify or enrich the data collected from your link.
                </Paragraph>
              </IntegrationComparisonCardDescription>
              <Spacer height={2} />
              <Paragraph>
                <b>Effort</b>: <Span color='brand'>No coding</Span>
              </Paragraph>
            </IntegrationComparisonCard>
          </Link>
          <Link to='/dashboard/getting-started/embedded-flow'>
            <IntegrationComparisonCard
              title='Embedded Flow'
              image={EmbeddedFlowImage}
              stickerLabel='Web apps'
            >
              <IntegrationComparisonCardDescription>
                <Paragraph>
                  Embed our drop-in iframe to collect user information in your web experience. Then,
                  use Persona to verify or enrich the data collected from your embedded flow.
                </Paragraph>
              </IntegrationComparisonCardDescription>
              <Spacer height={2} />
              <Paragraph>
                <b>Effort</b>: <Span color='brand'>Some coding</Span>
              </Paragraph>
            </IntegrationComparisonCard>
          </Link>
          <Link to='/dashboard/getting-started/mobile-sdks'>
            <IntegrationComparisonCard
              title='Mobile SDKs'
              image={MobileSDKsImage}
              stickerLabel='Native mobile apps'
            >
              <IntegrationComparisonCardDescription>
                <Paragraph>
                  Integrate with our mobile SDKs to seamlessly collect user information in your
                  native iOS or Android application. Then, use Persona to verify or enrich the data
                  collected from your application.
                </Paragraph>
              </IntegrationComparisonCardDescription>
              <Spacer height={2} />
              <Paragraph>
                <b>Effort</b>: <Span color='brand'>Some coding</Span>
              </Paragraph>
            </IntegrationComparisonCard>
          </Link>
          <Link to='/dashboard/getting-started/api'>
            <IntegrationComparisonCard
              title='Server APIs'
              image={APIImage}
              stickerLabel='Any platform'
            >
              <IntegrationComparisonCardDescription>
                <Paragraph>
                  Leverage our API to implement a homegrown information collection experience, then
                  pass that information to Persona to verify or enrich the data.
                </Paragraph>
              </IntegrationComparisonCardDescription>
              <Spacer height={2} />
              <Paragraph>
                <b>Effort</b>: <Span color='brand'>More coding</Span>
              </Paragraph>
            </IntegrationComparisonCard>
          </Link>
        </IntegrationComparisonCardsWrapper>
        <Spacer height={1} />
        <IntegrationCardHeader>Step 4: Listen for the results</IntegrationCardHeader>
        <Paragraph color='primary'>
          We recommend using <Link to='/dashboard/webhooks'>webhooks</Link> to listen for your
          results. In most cases, the events you’ll need to listen to are:
        </Paragraph>
        <UnorderedList>
          <ListItem>
            <code>inquiry.approved</code>: the Inquiry has been approved and the user should be
            allowed to proceed.
          </ListItem>
          <ListItem>
            <code>inquiry.declined</code>: the Inquiry has been declined.
          </ListItem>
          <ListItem>
            <code>inquiry.marked-for-review</code>: the Inquiry is now in a manual review state, due
            to your business logic. If using embedded flow, we recommend showing the user a message
            that their information is being reviewed. (Note that this status will eventually resolve
            into an approved or declined status.)
          </ListItem>
        </UnorderedList>
        <Spacer height={1} />
        <IntegrationCardHeader>Common considerations & best practices</IntegrationCardHeader>
        <UnorderedList>
          <ListItem>
            <a
              href='https://docs.withpersona.com/docs/prevent-users-from-creating-multiple-inquiries'
              target='_blank'
              rel='noreferrer'
            >
              Preventing users from going through Persona multiple times
            </a>
          </ListItem>
          <ListItem>
            <a
              href='https://docs.withpersona.com/docs/inquiries-resuming-inquiries'
              target='_blank'
              rel='noreferrer'
            >
              Resuming expired Inquiries
            </a>
          </ListItem>
        </UnorderedList>
      </IntegrationCardBody>
    </Card>
  );
};

interface IntegrationComparisonCardProps {
  title: string;
  image: ComponentType<SVGAttributes<SVGElement>>;
  stickerLabel: string;
  children: ReactNode;
}

const IntegrationComparisonCard = ({
  title,
  image,
  stickerLabel,
  children,
}: IntegrationComparisonCardProps) => {
  const Image = image;

  return (
    <IntegrationComparisonCardWrapper>
      <IntegrationComparisonCardImage height={140}>
        <Image />
        <StyledSticker variant='purple'>{stickerLabel}</StyledSticker>
      </IntegrationComparisonCardImage>
      <IntegrationComparisonCardBody>
        <Spacer height={1} />
        <Heading size='sm'>{title}</Heading>
        <Spacer height={1} />
        {children}
      </IntegrationComparisonCardBody>
    </IntegrationComparisonCardWrapper>
  );
};

const IntegrationCardBody = styled(Card.Body)`
  max-width: 1200px;
`;

const IntegrationCardHeader = styled('h2')`
  ${Typography.HeadingSmall};
  margin: 0px;
`;

const IntegrationComparisonCardsWrapper = styled('div')`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  column-gap: ${Spacing[4]};

  > * {
    flex: 1;
    margin-bottom: ${Spacing[2]};
    min-width: 240px;
    max-width: 300px;
  }
`;

const IntegrationComparisonCardWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.color.border.primary};
  border-radius: ${({ theme }) => theme.card.borderRadius};
  width: 100%;
  height: 100%;
  transition: ${TransitionDuration.Fast};
  :hover {
    transform: scale(1.02);
  }
`;

const IntegrationComparisonCardImage = styled(Card.Image)`
  position: relative;
`;

// styling for the Sticker inside IntegrationComparisonCardImage
const StyledSticker = styled(Sticker)`
  position: absolute;
  top: ${Spacing[4]};
  left: ${Spacing[4]};
`;

const IntegrationComparisonCardBody = styled('div')`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: ${Spacing[4]};
`;

// specify that the description should fill the remaining height of the card
const IntegrationComparisonCardDescription = styled('div')`
  flex: 1;
`;

const UnorderedList = styled('ul')`
  margin-left: ${Spacing[5]};
`;

const ListItem = styled('li')`
  margin: ${Spacing[2]} 0px;
`;
